import type { HelpGate } from "./articles";

/** A heading inside an article: its anchor id and plain text (for the search index). */
export interface HelpSection {
  id: string;
  title: string;
  text: string;
}

export interface ParsedArticle {
  title: string;
  summary: string;
  keywords: string[];
  /** Markdown body with gated sections already resolved for the reader. */
  body: string;
  /** Text before the first heading, then one entry per `##`/`###` heading. */
  intro: string;
  sections: HelpSection[];
}

const FRONT_MATTER = /^---\n([\s\S]*?)\n---\n?/;
const GATE_BLOCK = /<!-- gate: (\S+) -->\n?([\s\S]*?)<!-- \/gate -->\n?/g;

/** Heading text → anchor id ("Who can see it?" → "who-can-see-it"). Shared by the renderer and the index. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join("-");
}

/** `## Title` / `### Title` → "Title"; anything else → null. String ops, no backtracking regex. */
function headingText(line: string): string | null {
  let prefix = 0;
  if (line.startsWith("### ")) prefix = 4;
  else if (line.startsWith("## ")) prefix = 3;
  if (!prefix) return null;
  const text = line.slice(prefix).trim();
  return text || null;
}

/** `[text](url)` → text and `![alt](src)` → nothing, in one linear pass. */
function unlink(markdown: string): string {
  let out = "";
  let i = 0;
  while (i < markdown.length) {
    const open = markdown.indexOf("[", i);
    if (open < 0) break;
    const close = markdown.indexOf("](", open);
    const end = close < 0 ? -1 : markdown.indexOf(")", close + 2);
    const label = close < 0 ? "" : markdown.slice(open + 1, close);
    if (end < 0 || label.includes("[") || label.includes("\n")) {
      out += markdown.slice(i, open + 1);
      i = open + 1;
      continue;
    }
    const isImage = open > i && markdown[open - 1] === "!";
    out += isImage ? markdown.slice(i, open - 1) : markdown.slice(i, open) + label;
    i = end + 1;
  }
  return out + markdown.slice(i);
}

/** Markdown → readable plain text: what search matches (images are dropped, not indexed). */
export function plainText(markdown: string): string {
  return unlink(markdown)
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/^\s{0,3}(?:>\s?|[-*+]\s+|\d+\.\s+|#{1,6}\s+)/gm, "")
    .replace(/[*_`|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseFrontMatter(source: string): { fields: Record<string, string>; body: string } {
  const match = FRONT_MATTER.exec(source);
  if (!match) return { fields: {}, body: source };
  const fields: Record<string, string> = {};
  for (const line of (match[1] ?? "").split("\n")) {
    const colon = line.indexOf(":");
    if (colon > 0) fields[line.slice(0, colon).trim()] = line.slice(colon + 1).trim();
  }
  return { fields, body: source.slice(match[0].length) };
}

/** Every gate referenced inside an article body (for validation). */
export function gatesIn(source: string): string[] {
  return [...source.matchAll(GATE_BLOCK)].map((m) => m[1] ?? "");
}

/** Keep gated blocks the reader may see, drop the rest. */
export function resolveGates(body: string, allowed: (gate: HelpGate) => boolean): string {
  return body.replace(GATE_BLOCK, (_all, gate: string, inner: string) => (allowed(gate as HelpGate) ? inner : ""));
}

function splitSections(body: string): { intro: string; sections: HelpSection[] } {
  const intro: string[] = [];
  const sections: Array<{ id: string; title: string; lines: string[] }> = [];
  let inFence = false;
  for (const line of body.split("\n")) {
    if (line.trimStart().startsWith("```")) inFence = !inFence;
    const heading = inFence ? null : headingText(line);
    if (heading) {
      const title = plainText(heading);
      sections.push({ id: slugify(title), title, lines: [] });
    } else {
      (sections.at(-1)?.lines ?? intro).push(line);
    }
  }
  return {
    intro: plainText(intro.join("\n")),
    sections: sections.map(({ id, title, lines }) => ({ id, title, text: plainText(lines.join("\n")) })),
  };
}

export function parseArticle(source: string, allowed: (gate: HelpGate) => boolean): ParsedArticle {
  const { fields, body: raw } = parseFrontMatter(source);
  const body = resolveGates(raw, allowed).trim();
  return {
    title: fields.title ?? "",
    summary: fields.summary ?? "",
    keywords: (fields.keywords ?? "")
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean),
    body,
    ...splitSections(body),
  };
}
